import React, { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import PostDetailModal from "../components/Posts/PostDetailModal";
import classes from "./ReportsPage.module.css";

function buildReportRows(posts) {
  const rows = [];
  for (const post of posts || []) {
    const reports = Array.isArray(post.reports) ? post.reports : [];
    reports.forEach((report, index) => {
      rows.push({
        key: `${post.id}-${index}`,
        post,
        report,
        index,
      });
    });
  }
  return rows;
}

const ReportsPage = () => {
  const { postsData, deleteLoading, deletePostById } = useData();
  const [search, setSearch] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);

  const reportRows = useMemo(() => buildReportRows(postsData), [postsData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reportRows;
    return reportRows.filter(({ post, report }) => {
      const haystack = [
        post.title,
        post.uploader_details?.name,
        post.uploader_details?.email,
        report.report_type,
        report.report_message,
        report.reporter_details?.reporter_name,
        report.reporter_details?.reporter_email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [reportRows, search]);

  const reportedPostsCount = useMemo(() => {
    const ids = new Set(reportRows.map((row) => row.post.id));
    return ids.size;
  }, [reportRows]);

  return (
    <div className={`container p-3 ${classes.main}`}>
      <h4 className="mb-1">
        <strong>Reports</strong>
      </h4>
      <p className="small text-muted mb-3">
        App reports are stored on each post in the <code>reports</code> field.
        This page lists them across all videos.
      </p>

      <div className={classes.statsRow}>
        <span className="badge bg-danger">
          {reportRows.length} report{reportRows.length === 1 ? "" : "s"}
        </span>
        <span className="badge bg-secondary">
          {reportedPostsCount} reported video
          {reportedPostsCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="input-group mb-3">
        <input
          type="search"
          className="form-control"
          placeholder="Search: title / reporter / reason / message..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredRows.length === 0 ? (
        <div className="alert alert-light border">No reports yet.</div>
      ) : (
        <div className={classes.list}>
          {filteredRows.map(({ key, post, report, index }) => (
            <div className={classes.card} key={key}>
              <div className={classes.thumbWrap}>
                {post.thumbnail ? (
                  <img src={post.thumbnail} alt={post.title || "post"} />
                ) : (
                  <div className={classes.noThumb}>No image</div>
                )}
              </div>
              <div className={classes.body}>
                <div className={classes.titleRow}>
                  <h5 className="mb-1">{post.title || "Untitled"}</h5>
                  <span className="badge bg-warning text-dark">
                    Report #{index + 1}
                  </span>
                </div>
                <div className={classes.meta}>
                  <div>
                    <strong>Reason:</strong> {report.report_type || "—"}
                  </div>
                  <div>
                    <strong>Message:</strong> {report.report_message || "—"}
                  </div>
                  <div>
                    <strong>Reporter:</strong>{" "}
                    {report.reporter_details?.reporter_name || "—"}
                    {report.reporter_details?.reporter_email
                      ? ` · ${report.reporter_details.reporter_email}`
                      : ""}
                  </div>
                  <div>
                    <strong>Uploader:</strong>{" "}
                    {post.uploader_details?.name || "—"}
                    {post.uploader_details?.email
                      ? ` · ${post.uploader_details.email}`
                      : ""}
                  </div>
                </div>
                <div className={classes.actions}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setSelectedPost(post)}
                  >
                    View video
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={deleteLoading}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete video "${post.title || post.id}"?`
                        )
                      ) {
                        deletePostById(post);
                      }
                    }}
                  >
                    Delete video
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PostDetailModal
        show={!!selectedPost}
        post={selectedPost}
        onHide={() => setSelectedPost(null)}
      />
    </div>
  );
};

export default ReportsPage;
